import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  getCourseStandardSets,
  getCourseStandardsCoverage,
  getStandardSetSourceDocument,
  getStandardSets,
  putCourseStandardSets,
  type CourseStandardsCoverage as Coverage,
  type GlossaryTermRecord,
  type StandardCoverageLesson,
  type StandardRecord,
  type StandardSetSummary,
} from "../auth/api-client";
import { TeacherTabStrip } from "./TeacherTabStrip";

interface Props {
  courseId: string;
  bearer: string | null;
  archived: boolean;
  /** Changes when the course lesson sequence changes so coverage re-fetches. */
  lessonSequenceKey?: string;
}

type Tier = "intro" | "intermediate" | "culminating";
type CoverageTab = "standards" | "glossary";

const TIER_PROMPT: Record<Tier, string> = {
  intro: "Create an introductory lesson that first teaches",
  intermediate: "Create an intermediate practice lesson that reinforces",
  culminating: "Create a culminating project lesson that applies",
};

function tierFor(index: number, total: number): Tier {
  if (total <= 0) return "intro";
  const third = total / 3;
  if (index < third) return "intro";
  if (index < third * 2) return "intermediate";
  return "culminating";
}

function addLessonForStandardHref(
  courseId: string,
  setId: string,
  std: StandardRecord,
  tier: Tier
): string {
  const prompt = `${TIER_PROMPT[tier]} standard ${std.code}: ${std.description}`;
  const params = new URLSearchParams({
    addLesson: "end",
    standardSetId: setId,
    standardId: std.id,
    standardPrompt: prompt,
  });
  return `/teacher/courses/${encodeURIComponent(courseId)}/manage?${params.toString()}`;
}

function addLessonForGlossaryHref(courseId: string, setId: string, term: GlossaryTermRecord): string {
  const prompt = `Create a lesson that teaches and uses the glossary term ${term.term}: ${term.definition}`;
  const params = new URLSearchParams({
    addLesson: "end",
    standardSetId: setId,
    standardPrompt: prompt,
  });
  return `/teacher/courses/${encodeURIComponent(courseId)}/manage?${params.toString()}`;
}

type StandardRow = {
  standard: StandardRecord;
  lessons: StandardCoverageLesson[];
};

type GlossaryRow = {
  term: GlossaryTermRecord;
  lessons: StandardCoverageLesson[];
};

function mergeStandardRows(coverage: Coverage | null): StandardRow[] {
  if (!coverage) return [];
  const lessonMap = new Map(coverage.covered.map((c) => [c.standard.id, c.lessons]));
  const all = [...coverage.covered.map((c) => c.standard), ...coverage.uncovered];
  const byId = new Map(all.map((s) => [s.id, s]));
  return [...byId.values()]
    .sort((a, b) => a.position - b.position || a.code.localeCompare(b.code))
    .map((standard) => ({ standard, lessons: lessonMap.get(standard.id) ?? [] }));
}

function mergeGlossaryRows(coverage: Coverage | null): GlossaryRow[] {
  if (!coverage) return [];
  const glossaryCovered = coverage.glossaryCovered ?? [];
  const glossaryUncovered = coverage.glossaryUncovered ?? [];
  const lessonMap = new Map(glossaryCovered.map((c) => [c.term.id, c.lessons]));
  const all = [...glossaryCovered.map((c) => c.term), ...glossaryUncovered];
  const byId = new Map(all.map((t) => [t.id, t]));
  return [...byId.values()]
    .sort((a, b) => a.position - b.position || a.term.localeCompare(b.term))
    .map((term) => ({ term, lessons: lessonMap.get(term.id) ?? [] }));
}

function LessonCountBadge({ lessons }: { lessons: StandardCoverageLesson[] }) {
  if (lessons.length === 0) return null;
  const label = lessons.length === 1 ? "1 lesson" : `${lessons.length} lessons`;
  return (
    <span className="teacher-standards-lesson-count">
      <span className="teacher-standards-lesson-count-value" tabIndex={0}>
        {label}
      </span>
      <span className="teacher-standards-lesson-tooltip" role="tooltip">
        <span className="teacher-standards-lesson-tooltip-title">Covered in</span>
        <ul>
          {lessons.map((lesson) => (
            <li key={lesson.id}>{lesson.title}</li>
          ))}
        </ul>
      </span>
    </span>
  );
}

function CoverageProgress({
  pct,
  coveredCount,
  total,
  itemLabel,
}: {
  pct: number;
  coveredCount: number;
  total: number;
  itemLabel: string;
}) {
  return (
    <div className="teacher-standards-progress">
      <div className="teacher-standards-progress-head">
        <strong>{pct}% covered</strong>
        <span className="muted small">
          {coveredCount} of {total} {itemLabel} have a lesson in this course
        </span>
      </div>
      <div className="teacher-standards-progress-bar">
        <div className="teacher-standards-progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function CourseStandardsCoverage({
  courseId,
  bearer,
  archived,
  lessonSequenceKey = "",
}: Props) {
  const [allSets, setAllSets] = useState<StandardSetSummary[]>([]);
  const [linkedIds, setLinkedIds] = useState<Set<string>>(new Set());
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [viewSetId, setViewSetId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<CoverageTab>("standards");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftIds, setDraftIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const loadCoverage = useCallback(
    async (setId?: string) => {
      if (!bearer) return;
      const cov = await getCourseStandardsCoverage(bearer, courseId, setId);
      setCoverage(cov);
      setViewSetId(cov.standardSetId ?? "");
    },
    [bearer, courseId]
  );

  useEffect(() => {
    let cancelled = false;
    if (!bearer) return;
    (async () => {
      setError(null);
      try {
        const [{ standardSets: all }, { standardSets: linked }] = await Promise.all([
          getStandardSets(bearer),
          getCourseStandardSets(bearer, courseId),
        ]);
        if (cancelled) return;
        setAllSets(all);
        setLinkedIds(new Set(linked.map((s) => s.id)));
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load standard sets");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, courseId]);

  useEffect(() => {
    let cancelled = false;
    if (!bearer) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await loadCoverage(viewSetId || undefined);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load standards coverage");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, courseId, lessonSequenceKey, loadCoverage]);

  const linkedSets = useMemo(() => allSets.filter((s) => linkedIds.has(s.id)), [allSets, linkedIds]);

  const activeSet = useMemo(() => {
    const fromCoverage = coverage?.standardSets.find((s) => s.id === viewSetId);
    if (fromCoverage) return fromCoverage;
    return linkedSets.find((s) => s.id === viewSetId) ?? linkedSets[0] ?? null;
  }, [coverage?.standardSets, linkedSets, viewSetId]);

  const handleSaveAssociation = useCallback(async () => {
    if (!bearer) return;
    setSaving(true);
    setError(null);
    try {
      await putCourseStandardSets(bearer, courseId, [...draftIds]);
      setLinkedIds(new Set(draftIds));
      setEditing(false);
      await loadCoverage();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save standard sets");
    } finally {
      setSaving(false);
    }
  }, [bearer, courseId, draftIds, loadCoverage]);

  const startEditing = useCallback(() => {
    setDraftIds(new Set(linkedIds));
    setEditing(true);
  }, [linkedIds]);

  const handleViewSourceDocument = useCallback(async () => {
    if (!bearer || !viewSetId) return;
    setPdfBusy(true);
    setError(null);
    try {
      const { viewUrl } = await getStandardSetSourceDocument(bearer, viewSetId);
      window.open(viewUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open standards document");
    } finally {
      setPdfBusy(false);
    }
  }, [bearer, viewSetId]);

  const standardRows = useMemo(() => mergeStandardRows(coverage), [coverage]);
  const glossaryRows = useMemo(() => mergeGlossaryRows(coverage), [coverage]);

  const uncoveredStandardIndex = useMemo(() => {
    const map = new Map<string, number>();
    let i = 0;
    for (const row of standardRows) {
      if (row.lessons.length === 0) {
        map.set(row.standard.id, i);
        i += 1;
      }
    }
    return map;
  }, [standardRows]);

  const coveredStandardCount = coverage?.covered.length ?? 0;
  const uncoveredStandardCount = coverage?.uncovered.length ?? 0;
  const standardTotal = coveredStandardCount + uncoveredStandardCount;
  const standardPct = Math.round((coverage?.coverageRatio ?? 0) * 100);

  const coveredGlossaryCount = coverage?.glossaryCovered?.length ?? 0;
  const uncoveredGlossaryCount = coverage?.glossaryUncovered?.length ?? 0;
  const glossaryTotal = coveredGlossaryCount + uncoveredGlossaryCount;
  const glossaryPct = Math.round((coverage?.glossaryCoverageRatio ?? 0) * 100);

  return (
    <section className="teacher-panel teacher-panel--flush teacher-standards-coverage-panel">
      <div className="teacher-template-sequence-header">
        <h2 className="teacher-panel-title">Standards coverage</h2>
      </div>

      {error ? <p className="form-error teacher-pad">{error}</p> : null}

      {loading ? (
        <p className="muted teacher-pad">Loading…</p>
      ) : editing ? (
        <div className="teacher-pad teacher-standards-assoc">
          {allSets.length === 0 ? (
            <p className="muted">
              No published standard sets are available yet. Ask an administrator to publish one.
            </p>
          ) : (
            <>
              <p className="muted small">Select the published standard sets this course aligns to.</p>
              <ul className="lesson-standards-list">
                {allSets.map((s) => (
                  <li key={s.id}>
                    <label className="lesson-standard-toggle">
                      <input
                        type="checkbox"
                        checked={draftIds.has(s.id)}
                        onChange={(e) => {
                          setDraftIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(s.id);
                            else next.delete(s.id);
                            return next;
                          });
                        }}
                      />
                      <span>
                        <strong>{s.name}</strong>
                        <span className="lesson-standard-desc">
                          {s.standardsCount} standards · {s.glossaryCount} glossary terms
                        </span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="teacher-standards-assoc-actions">
            <button type="button" className="btn primary tight" disabled={saving} onClick={() => void handleSaveAssociation()}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn secondary tight" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : linkedSets.length === 0 ? (
        <div className="teacher-pad teacher-standards-empty-cta">
          <p className="muted">
            This course is not aligned to a standard set yet. Add one to track coverage against your lesson sequence.
          </p>
          {!archived ? (
            <div className="teacher-standards-empty-cta-action">
              <button type="button" className="btn primary tight" onClick={startEditing}>
                Add standard set
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="teacher-pad teacher-standards-coverage">
          {(coverage?.standardSets.length ?? 0) > 1 ? (
            <label className="teacher-standards-coverage-select">
              <span className="muted small">Standard set</span>
              <select
                value={viewSetId}
                onChange={(e) => {
                  const next = e.target.value;
                  setViewSetId(next);
                  void loadCoverage(next);
                }}
              >
                {coverage?.standardSets.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {activeSet ? (
            <div className="teacher-standards-set-header">
              <h3 className="teacher-standards-set-title">{activeSet.name}</h3>
              {!archived ? (
                <div className="teacher-standards-set-actions">
                  <button type="button" className="btn secondary tight" onClick={startEditing}>
                    Edit Standards
                  </button>
                  {coverage?.hasSourceDocument ? (
                    <button
                      type="button"
                      className="btn secondary tight"
                      disabled={pdfBusy}
                      onClick={() => void handleViewSourceDocument()}
                    >
                      {pdfBusy ? "Opening…" : "View Standards"}
                    </button>
                  ) : null}
                </div>
              ) : coverage?.hasSourceDocument ? (
                <div className="teacher-standards-set-actions">
                  <button
                    type="button"
                    className="btn secondary tight"
                    disabled={pdfBusy}
                    onClick={() => void handleViewSourceDocument()}
                  >
                    {pdfBusy ? "Opening…" : "View Standards"}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          <TeacherTabStrip
            tabs={[
              { id: "standards", label: "Standards" },
              { id: "glossary", label: "Glossary" },
            ]}
            active={activeTab}
            onSelect={(id) => setActiveTab(id as CoverageTab)}
            ariaLabel="Coverage view"
          />

          {activeTab === "standards" ? (
            <>
              {standardTotal > 0 ? (
                <CoverageProgress
                  pct={standardPct}
                  coveredCount={coveredStandardCount}
                  total={standardTotal}
                  itemLabel="standards"
                />
              ) : null}

              {standardRows.length > 0 ? (
                <div className="teacher-standards-checklist-wrap">
                  <p className="muted small teacher-standards-checklist-lede">
                    Checked standards are linked from at least one lesson in this course. Hover the lesson count to see
                    which lessons cover each standard.
                  </p>
                  <ul className="teacher-standards-checklist">
                    {standardRows.map((row) => {
                      const covered = row.lessons.length > 0;
                      const uncoveredIdx = uncoveredStandardIndex.get(row.standard.id);
                      const tier =
                        uncoveredIdx !== undefined
                          ? tierFor(uncoveredIdx, uncoveredStandardCount)
                          : ("intro" as Tier);
                      return (
                        <li
                          key={row.standard.id}
                          className={`teacher-standards-checklist-item${covered ? " is-covered" : ""}`}
                        >
                          <label className="teacher-standards-checklist-main">
                            <input
                              type="checkbox"
                              checked={covered}
                              readOnly
                              disabled
                              aria-label={`${row.standard.code} coverage`}
                            />
                            <span className="teacher-standards-checklist-body">
                              <strong>{row.standard.code}</strong>
                              <span className="lesson-standard-desc">{row.standard.description}</span>
                            </span>
                          </label>
                          <div className="teacher-standards-checklist-meta">
                            {covered ? (
                              <LessonCountBadge lessons={row.lessons} />
                            ) : (
                              <>
                                <span className="muted small teacher-standards-lesson-count-empty">No lessons</span>
                                {!archived && viewSetId ? (
                                  <Link
                                    to={addLessonForStandardHref(courseId, viewSetId, row.standard, tier)}
                                    className="btn secondary tight"
                                    style={{ textDecoration: "none", whiteSpace: "nowrap" }}
                                  >
                                    Add lesson
                                  </Link>
                                ) : null}
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <p className="muted small">No standards in this set.</p>
              )}

              {uncoveredStandardCount === 0 && standardRows.length > 0 ? (
                <p className="muted">Every standard in this set is covered by at least one lesson. Nice work.</p>
              ) : null}
            </>
          ) : (
            <>
              {glossaryTotal > 0 ? (
                <CoverageProgress
                  pct={glossaryPct}
                  coveredCount={coveredGlossaryCount}
                  total={glossaryTotal}
                  itemLabel="glossary terms"
                />
              ) : null}

              {glossaryRows.length > 0 ? (
                <div className="teacher-standards-checklist-wrap">
                  <p className="muted small teacher-standards-checklist-lede">
                    Checked terms are linked from at least one lesson in this course. Hover the lesson count to see
                    which lessons use each term.
                  </p>
                  <ul className="teacher-standards-checklist">
                    {glossaryRows.map((row) => {
                      const covered = row.lessons.length > 0;
                      return (
                        <li
                          key={row.term.id}
                          className={`teacher-standards-checklist-item${covered ? " is-covered" : ""}`}
                        >
                          <label className="teacher-standards-checklist-main">
                            <input
                              type="checkbox"
                              checked={covered}
                              readOnly
                              disabled
                              aria-label={`${row.term.term} coverage`}
                            />
                            <span className="teacher-standards-checklist-body">
                              <strong>{row.term.term}</strong>
                              <span className="lesson-standard-desc">{row.term.definition}</span>
                            </span>
                          </label>
                          <div className="teacher-standards-checklist-meta">
                            {covered ? (
                              <LessonCountBadge lessons={row.lessons} />
                            ) : (
                              <>
                                <span className="muted small teacher-standards-lesson-count-empty">No lessons</span>
                                {!archived && viewSetId ? (
                                  <Link
                                    to={addLessonForGlossaryHref(courseId, viewSetId, row.term)}
                                    className="btn secondary tight"
                                    style={{ textDecoration: "none", whiteSpace: "nowrap" }}
                                  >
                                    Add lesson
                                  </Link>
                                ) : null}
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <p className="muted small">No glossary terms in this set.</p>
              )}

              {uncoveredGlossaryCount === 0 && glossaryRows.length > 0 ? (
                <p className="muted">Every glossary term in this set is used by at least one lesson. Nice work.</p>
              ) : null}
            </>
          )}
        </div>
      )}
    </section>
  );
}
